import PagedResultSet from 'src/models/common/PagedResultSet';
import CountyModel from 'src/models/County';
import { ODataOptions } from 'src/util/ODataBuilder';
import { Reducer, Action } from 'redux';
import { IRootState } from './index';
import { fork, call, takeEvery, put, all, select, delay, putResolve } from 'redux-saga/effects';

import { actionCreators as layoutActionCreators } from '../reducers/Layout';
import { selectors as authSelectors } from '../reducers/Auth';

import Api from '../api';

// Store
export interface ICountyState {
    stateCounties: PagedResultSet<CountyModel>;
    projectCounties: PagedResultSet<CountyModel>;
    townshipCounties: PagedResultSet<CountyModel>;
    sectionCounties: PagedResultSet<CountyModel>;
    easternMapCounties: PagedResultSet<CountyModel>;
    tractCounties: PagedResultSet<CountyModel>;
    selectedCounty: number;
}

const initialState: ICountyState = {
    stateCounties: undefined,
    projectCounties: undefined,
    townshipCounties: undefined,
    sectionCounties: undefined,
    easternMapCounties: undefined,
    tractCounties: undefined,
    selectedCounty: 0,
};

// Action Types
export enum actionTypes {
    INITIALIZE_COUNTIES = '[county] INITIALIZE_COUNTIES',
    GET_STATE_COUNTIES = '[county] GET_STATE_COUNTIES',
    GET_STATE_COUNTIES_SUCCESS = '[county] GET_STATE_COUNTIES_SUCCESS',
    GET_PROJECT_COUNTIES = '[county] GET_PROJECT_COUNTIES',
    GET_PROJECT_COUNTIES_SUCCESS = '[county] GET_PROJECT_COUNTIES_SUCCESS',
    GET_TOWNSHIP_COUNTIES = '[county] GET_TOWNSHIP_COUNTIES',
    GET_TOWNSHIP_COUNTIES_SUCCESS = '[county] GET_TOWNSHIP_COUNTIES_SUCCESS',
    GET_SECTION_COUNTIES = '[county] GET_SECTION_COUNTIES',
    GET_SECTION_COUNTIES_SUCCESS = '[county] GET_SECTION_COUNTIES_SUCCESS',
    GET_EASTERN_MAP_COUNTIES = '[county] GET_EASTERN_MAP_COUNTIES',
    GET_EASTERN_MAP_COUNTIES_SUCCESS = '[county] GET_EASTERN_MAP_COUNTIES_SUCCESS',
    GET_TRACT_COUNTIES = '[county] GET_TRACT_COUNTIES',
    GET_TRACT_COUNTIES_SUCCESS = '[county] GET_TRACT_COUNTIES_SUCCESS',
    CREATE_COUNTY = '[tract] CREATE_COUNTY',
    CREATE_COUNTY_SUCCESS = '[tract] CREATE_COUNTY_SUCCESS',
}

// Action Creator
export const actionCreators = {
    initializeCounties: () => {
        return { type: actionTypes.INITIALIZE_COUNTIES };
    },
    getStateCounties: (stateId: number, options: ODataOptions) => {
        return { type: actionTypes.GET_STATE_COUNTIES, payload: { stateId, options } };
    },
    getStateCountiesSuccess: (counties: PagedResultSet<CountyModel>) => {
        return { type: actionTypes.GET_STATE_COUNTIES_SUCCESS, payload: counties };
    },
    getProjectCounties: (clientNumber: number, projectNumber: number, options: ODataOptions) => {
        return { type: actionTypes.GET_PROJECT_COUNTIES, payload: { clientNumber, projectNumber, options } };
    },
    getProjectCountiesSuccess: (counties: PagedResultSet<CountyModel>) => {
        return { type: actionTypes.GET_PROJECT_COUNTIES_SUCCESS, payload: counties };
    },

    getTownshipCounties: (clientNumber: number, projectNumber: number, townshipId: number, options: ODataOptions) => {
        return { type: actionTypes.GET_TOWNSHIP_COUNTIES, payload: { clientNumber, projectNumber, townshipId, options } };
    },
    getTownshipCountiesSuccess: (counties: PagedResultSet<CountyModel>) => {
        return { type: actionTypes.GET_TOWNSHIP_COUNTIES_SUCCESS, payload: counties };
    },

    getSectionCounties: (clientNumber: number, projectNumber: number, sectionNumber: number, options: ODataOptions) => {
        return { type: actionTypes.GET_SECTION_COUNTIES, payload: { clientNumber, projectNumber, sectionNumber, options } };
    },
    getSectionCountiesSuccess: (counties: PagedResultSet<CountyModel>) => {
        return { type: actionTypes.GET_SECTION_COUNTIES_SUCCESS, payload: counties };
    },

    getEasternMapCounties: (clientNumber: number, projectNumber: number, mapNumber: number, options: ODataOptions) => {
        return { type: actionTypes.GET_EASTERN_MAP_COUNTIES, payload: { clientNumber, projectNumber, mapNumber, options } };
    },
    getEasternMapCountiesSuccess: (counties: PagedResultSet<CountyModel>) => {
        return { type: actionTypes.GET_EASTERN_MAP_COUNTIES_SUCCESS, payload: counties };
    },

    getTractCounties: (
        clientNumber: number,
        projectNumber: number,
        townshipNumber: number,
        sectionNumber: number,
        tractNumber: number,
        options: ODataOptions
    ) => {
        return {
            type: actionTypes.GET_TRACT_COUNTIES,
            payload: { clientNumber, projectNumber, townshipNumber, sectionNumber, tractNumber, options },
        };
    },
    getTractCountiesSuccess: (counties: PagedResultSet<CountyModel>) => {
        return { type: actionTypes.GET_TRACT_COUNTIES_SUCCESS, payload: counties };
    },
    createCounty: (clientNumber: number, projectNumber: number, county: CountyModel) => {
        return { type: actionTypes.CREATE_COUNTY, payload: { clientNumber, projectNumber, county } };
    },
    createCountySuccess: (newCounty: CountyModel) => {
        return { type: actionTypes.CREATE_COUNTY_SUCCESS, payload: newCounty };
    },
};

// Reducer
const reducer: Reducer<ICountyState> = (state: ICountyState = initialState, action: Action): ICountyState => {
    switch (action.type) {
        case actionTypes.INITIALIZE_COUNTIES:
            return {
                ...state,
                stateCounties: undefined,
                projectCounties: undefined,
                townshipCounties: undefined,
                sectionCounties: undefined,
                tractCounties: undefined,
            };
        case actionTypes.GET_STATE_COUNTIES_SUCCESS:
            const getStateCountiesPayload = (action as { type: string; payload: PagedResultSet<CountyModel> }).payload;
            return {
                ...state,
                stateCounties: getStateCountiesPayload,
            };
        case actionTypes.GET_PROJECT_COUNTIES_SUCCESS:
            const getProjectCountiesPayload = (action as { type: string; payload: PagedResultSet<CountyModel> }).payload;
            return {
                ...state,
                projectCounties: getProjectCountiesPayload,
            };
        case actionTypes.GET_TOWNSHIP_COUNTIES_SUCCESS:
            const getTownshipCountiesPayload = (action as { type: string; payload: PagedResultSet<CountyModel> }).payload;
            return {
                ...state,
                townshipCounties: getTownshipCountiesPayload,
            };
        case actionTypes.GET_SECTION_COUNTIES_SUCCESS:
            const getSectionCountiesPayload = (action as { type: string; payload: PagedResultSet<CountyModel> }).payload;
            return {
                ...state,
                sectionCounties: getSectionCountiesPayload,
            };
        case actionTypes.GET_EASTERN_MAP_COUNTIES_SUCCESS:
            const getEasternMapCountiesPayload = (action as { type: string; payload: PagedResultSet<CountyModel> }).payload;
            return {
                ...state,
                easternMapCounties: getEasternMapCountiesPayload,
            };
        case actionTypes.GET_TRACT_COUNTIES_SUCCESS:
            const getTractCountiesPayload = (action as { type: string; payload: PagedResultSet<CountyModel> }).payload;
            return {
                ...state,
                tractCounties: getTractCountiesPayload,
            };
        case actionTypes.CREATE_COUNTY_SUCCESS:
            const newCounty = (action as { type: string; payload: CountyModel }).payload;
            return {
                ...state,
                selectedCounty: newCounty.id,
            };
        default:
            return state;
    }
};

export default reducer;

// Selectors
const stateCountiesSelector = (state: IRootState): PagedResultSet<CountyModel> => state.county.stateCounties;
const projectCountiesSelector = (state: IRootState): PagedResultSet<CountyModel> => state.county.projectCounties;
const townshipCountiesSelector = (state: IRootState): PagedResultSet<CountyModel> => state.county.townshipCounties;
const sectionCountiesSelector = (state: IRootState): PagedResultSet<CountyModel> => state.county.sectionCounties;
const tractCountiesSelector = (state: IRootState): PagedResultSet<CountyModel> => state.county.tractCounties;
const selectedCounty = (state: IRootState): number => state.county.selectedCounty;

export const selectors = {
    stateCounties: stateCountiesSelector,
    projectCounties: projectCountiesSelector,
    townshipCounties: townshipCountiesSelector,
    sectionCounties: sectionCountiesSelector,
    tractCounties: tractCountiesSelector,
    selectedCounty: selectedCounty,
};

// Sagas
const sagas = {
    getStateCounties: function* (action: { type: string; payload: { stateId: number; options: ODataOptions } }): unknown {
        yield call(layoutActionCreators.waitIniitalLoadState);
        const jwt = yield select(authSelectors.jwt);
        const { options, stateId } = action.payload;
        const [result] = yield all([call(Api.County.getCountiesForState, stateId, options, jwt), delay(200)]);
        yield put(actionCreators.getStateCountiesSuccess(result));
    },
    getProjectCounties: function* (action: {
        type: string;
        payload: { clientNumber: number; projectNumber: number; options: ODataOptions };
    }): unknown {
        yield call(layoutActionCreators.waitIniitalLoadState);
        const jwt = yield select(authSelectors.jwt);
        const { clientNumber, projectNumber, options } = action.payload;
        const [result] = yield all([call(Api.County.getCountiesForProject, clientNumber, projectNumber, options, jwt), delay(200)]);
        yield put(actionCreators.getProjectCountiesSuccess(result));
    },
    getTownshipCounties: function* (action: {
        type: string;
        payload: { clientNumber: number; projectNumber: number; townshipId: number; options: ODataOptions };
    }): unknown {
        yield call(layoutActionCreators.waitIniitalLoadState);
        const jwt = yield select(authSelectors.jwt);
        const { clientNumber, projectNumber, townshipId, options } = action.payload;
        const [result] = yield all([call(Api.County.getCountiesForTownship, clientNumber, projectNumber, townshipId, options, jwt), delay(200)]);
        yield put(actionCreators.getTownshipCountiesSuccess(result));
    },
    getEasternMapCounties: function* (action: {
        type: string;
        payload: { clientNumber: number; projectNumber: number; mapNumber: number; options: ODataOptions };
    }): unknown {
        yield call(layoutActionCreators.waitIniitalLoadState);
        const jwt = yield select(authSelectors.jwt);
        const { clientNumber, projectNumber, mapNumber, options } = action.payload;
        const [result] = yield all([call(Api.County.getCountiesForEasternMap, clientNumber, projectNumber, mapNumber, options, jwt), delay(200)]);
        yield put(actionCreators.getEasternMapCountiesSuccess(result));
    },
    getSectionCounties: function* (action: {
        type: string;
        payload: { clientNumber: number; projectNumber: number; sectionNumber: number; options: ODataOptions };
    }): unknown {
        yield call(layoutActionCreators.waitIniitalLoadState);
        const jwt = yield select(authSelectors.jwt);
        const { clientNumber, projectNumber, sectionNumber, options } = action.payload;
        const [result] = yield all([call(Api.County.getCountiesForSection, clientNumber, projectNumber, sectionNumber, options, jwt), delay(200)]);
        yield put(actionCreators.getSectionCountiesSuccess(result));
    },
    // tslint:disable-next-line:max-line-length
    getTractCounties: function* (action: {
        type: string;
        payload: {
            clientNumber: number;
            projectNumber: number;
            townshipNumber: number;
            sectionNumber: number;
            tractNumber: number;
            options: ODataOptions;
        };
    }): unknown {
        yield call(layoutActionCreators.waitIniitalLoadState);
        const jwt = yield select(authSelectors.jwt);
        const { clientNumber, projectNumber, townshipNumber, sectionNumber, tractNumber, options } = action.payload;
        const [result] = yield all([
            call(Api.County.getCountiesForTract, clientNumber, projectNumber, townshipNumber, sectionNumber, tractNumber, options, jwt),
            delay(200),
        ]);
        yield put(actionCreators.getTractCountiesSuccess(result));
    },
    createCounty: function* (action: { type: string; payload: { clientNumber: number; projectNumber: number; county: CountyModel } }): unknown {
        const countyOptions = new ODataOptions();

        yield call(layoutActionCreators.waitIniitalLoadState);
        const jwt = yield select(authSelectors.jwt);
        const { clientNumber, projectNumber, county } = action.payload;
        const [result] = yield all([call(Api.County.createCounty, clientNumber, projectNumber, county, jwt), delay(200)]);
        yield putResolve(actionCreators.getProjectCounties(clientNumber, projectNumber, countyOptions));
        if (result) {
            const newCounty = action.payload.county;
            newCounty.id = result.id;
            yield put(actionCreators.createCountySuccess(newCounty));
        }
        yield put(layoutActionCreators.hideDialog());
    },
};

// Watches
const watches = {
    getStateCounties: function* () {
        yield takeEvery(actionTypes.GET_STATE_COUNTIES, sagas.getStateCounties);
    },
    getProjectCounties: function* () {
        yield takeEvery(actionTypes.GET_PROJECT_COUNTIES, sagas.getProjectCounties);
    },
    getTownshipCounties: function* () {
        yield takeEvery(actionTypes.GET_TOWNSHIP_COUNTIES, sagas.getTownshipCounties);
    },
    getSectionCounties: function* () {
        yield takeEvery(actionTypes.GET_SECTION_COUNTIES, sagas.getSectionCounties);
    },
    getEasternMapCounties: function* () {
        yield takeEvery(actionTypes.GET_EASTERN_MAP_COUNTIES, sagas.getEasternMapCounties);
    },
    getTractCounties: function* () {
        yield takeEvery(actionTypes.GET_TRACT_COUNTIES, sagas.getTractCounties);
    },
    createCounty: function* () {
        yield takeEvery(actionTypes.CREATE_COUNTY, sagas.createCounty);
    },
};

// Forks
export function* saga() {
    yield fork(watches.getStateCounties);
    yield fork(watches.getProjectCounties);
    yield fork(watches.getTownshipCounties);
    yield fork(watches.getSectionCounties);
    yield fork(watches.getEasternMapCounties);
    yield fork(watches.getTractCounties);
    yield fork(watches.createCounty);
}



// WEBPACK FOOTER //
// ./src/reducers/County.ts